/**
 * Module-level snapshot of the signed-in user.
 *
 * Why this exists: the friendly-error layer (`showApiError` / `friendlyMessage`)
 * is made of plain functions, not React hooks, so it can't call `useAuth()`.
 * The AuthProvider keeps this snapshot in sync (one `useEffect`), and the error
 * layer reads it to decide WHICH message + CTA to show based on the viewer's
 * role — "who can fix this?" (Stripe/Shopify pattern).
 *
 * It is intentionally NOT a security boundary — the backend authorizes every
 * request. This only tailors the user-facing copy.
 */

/** Minimal shape we read — structurally compatible with `User` from useAuth. */
export interface CurrentUserSnapshot {
  id?: string;
  role?: string | null;
  is_organization_owner?: boolean;
  is_super_admin?: boolean;
  [k: string]: unknown;
}

let snapshot: CurrentUserSnapshot | null = null;

/** Called by the AuthProvider whenever the signed-in user changes (login/logout/refresh). */
export function setCurrentUser(user: CurrentUserSnapshot | null): void {
  snapshot = user;
}

/** Read the current user outside React (e.g. from the error layer). */
export function getCurrentUser(): CurrentUserSnapshot | null {
  return snapshot;
}

/**
 * The four audiences error copy is tailored for. Maps wenext roles onto a
 * single "who is this?" axis:
 *   owner       -> profiles.is_organization_owner
 *   super_admin -> profiles.is_super_admin
 *   admin       -> role === "ADMIN" (case-insensitive)
 *   agent       -> everyone else signed in ("client"/agent — limited power)
 *   anonymous   -> not signed in
 */
export type Audience = "owner" | "super_admin" | "admin" | "agent" | "anonymous";

export function audienceOf(user?: CurrentUserSnapshot | null): Audience {
  if (!user) return "anonymous";
  if (user.is_super_admin) return "super_admin";
  if (user.is_organization_owner) return "owner";
  const role = (user.role || "").toString().trim().toUpperCase();
  if (role === "ADMIN") return "admin";
  return "agent";
}

/**
 * Can this person resolve org-level problems (connect an integration, change
 * settings, manage billing)? Owner / super-admin / admin can; agents cannot.
 * This is the gate that decides whether we show an actionable CTA or an
 * "ask your admin" message.
 */
export function canManage(user?: CurrentUserSnapshot | null): boolean {
  const a = audienceOf(user);
  return a === "owner" || a === "super_admin" || a === "admin";
}
